/**
 * The post pipeline's shaders (render/PostPipeline.ts). Every pass is a fullscreen triangle; the
 * vertex shader is shared and ignores the camera. Depth is read straight from the scene target's
 * depth texture, so no pass needs the scene drawn a second time.
 */

export const fullscreenVertex = /* glsl */ `
varying vec2 vUv;
void main() {
  vUv = position.xy * 0.5 + 0.5;
  gl_Position = vec4(position.xy, 0.0, 1.0);
}
`;

const depthGlsl = /* glsl */ `
uniform sampler2D tDepth;
uniform mat4 uInvProj;
uniform float uNear;
uniform float uFar;
float readDepth(vec2 uv) { return texture2D(tDepth, uv).r; }
vec3 viewPosition(vec2 uv, float depth) {
  vec4 clip = vec4(uv * 2.0 - 1.0, depth * 2.0 - 1.0, 1.0);
  vec4 v = uInvProj * clip;
  return v.xyz / v.w;
}
// 0 at the near plane, 1 at the far plane, linear in distance.
float linearDepth(vec3 viewPos) { return (-viewPos.z - uNear) / (uFar - uNear); }
`;

/**
 * Screen-space AO at half resolution from depth alone. The normal comes from depth derivatives,
 * taking on each axis the neighbour whose depth is closer to the centre's, so a silhouette edge
 * picks one side instead of a normal pointing along the view ray. The kernel is a jittered spiral
 * of SAMPLES × RINGS taps whose screen radius shrinks with distance; the occlusion term is
 * f(r)³ · max(v·n − bias, 0)/(fade + |v|²), summed and normalised, with a depth-proximity falloff
 * so a far background behind an edge does not occlude the foreground.
 */
export const ssaoFragment = /* glsl */ `
${depthGlsl}
uniform vec2 uTexel;
uniform mat4 uProj;
uniform float uRadius;
uniform float uBias;
uniform float uFade;
uniform float uMinRadiusScale;
uniform vec2 uDistanceCutoff;
uniform vec2 uProximityCutoff;
varying vec2 vUv;

vec3 reconstructNormal(vec2 uv, vec3 p) {
  vec2 dx = vec2(uTexel.x, 0.0), dy = vec2(0.0, uTexel.y);
  vec3 pl = viewPosition(uv - dx, readDepth(uv - dx));
  vec3 pr = viewPosition(uv + dx, readDepth(uv + dx));
  vec3 pd = viewPosition(uv - dy, readDepth(uv - dy));
  vec3 pu = viewPosition(uv + dy, readDepth(uv + dy));
  vec3 ddx = abs(pr.z - p.z) < abs(p.z - pl.z) ? pr - p : p - pl;
  vec3 ddy = abs(pu.z - p.z) < abs(p.z - pd.z) ? pu - p : p - pd;
  return normalize(cross(ddx, ddy));
}

float interleavedGradientNoise(vec2 px) {
  return fract(52.9829189 * fract(dot(px, vec2(0.06711056, 0.00583715))));
}

void main() {
  float depth = readDepth(vUv);
  if (depth >= 1.0) { gl_FragColor = vec4(0.0); return; }
  vec3 p = viewPosition(vUv, depth);
  float ld = linearDepth(p);
  if (ld >= uDistanceCutoff.y) { gl_FragColor = vec4(0.0); return; }
  vec3 n = reconstructNormal(vUv, p);

  float radiusScale = 1.0 - smoothstep(0.0, uDistanceCutoff.y, ld);
  radiusScale = radiusScale * (1.0 - uMinRadiusScale) + uMinRadiusScale;
  // uRadius is a fraction of the screen height; the same disc in view space at this depth.
  float screenRadius = uRadius * radiusScale;
  vec2 uvRadius = vec2(screenRadius * uProj[1][1] / uProj[0][0], screenRadius);
  float R = screenRadius * (-p.z) * 2.0 / uProj[1][1];
  float R2 = R * R;

  float noise = interleavedGradientNoise(gl_FragCoord.xy);
  float occlusion = 0.0;
  int taps = 0;
  for (int i = 0; i < SAMPLES; i++) {
    float t = (float(i) + 0.5) / float(SAMPLES);
    float angle = (t * float(RINGS) + noise) * 6.2831853;
    vec2 uv = vUv + vec2(cos(angle), sin(angle)) * sqrt(t) * uvRadius;
    if (uv.x < 0.0 || uv.x > 1.0 || uv.y < 0.0 || uv.y > 1.0) continue;
    float sd = readDepth(uv);
    if (sd >= 1.0) continue;
    vec3 q = viewPosition(uv, sd);
    float proximity = abs(ld - linearDepth(q));
    if (proximity >= uProximityCutoff.y) continue;
    float falloff = 1.0 - smoothstep(uProximityCutoff.x, uProximityCutoff.y, proximity);
    vec3 v = q - p;
    float vv = dot(v, v);
    float vn = dot(v, n) - uBias;
    float f = max(R2 - vv, 0.0) / R2;
    occlusion += f * f * f * max(vn / (uFade + vv), 0.0) * falloff;
    taps++;
  }
  float ao = occlusion / (4.0 * max(float(taps), 1.0));
  ao *= 1.0 - smoothstep(uDistanceCutoff.x, uDistanceCutoff.y, ld);
  gl_FragColor = vec4(ao, 0.0, 0.0, 1.0);
}
`;

/** 3×3 depth-aware blur of the half-resolution AO: neighbours across a depth step do not contribute. */
export const aoBlurFragment = /* glsl */ `
${depthGlsl}
uniform sampler2D tAO;
uniform vec2 uTexel;
varying vec2 vUv;
void main() {
  float z0 = -viewPosition(vUv, readDepth(vUv)).z;
  float sum = 0.0, weight = 0.0;
  for (int y = -1; y <= 1; y++) {
    for (int x = -1; x <= 1; x++) {
      vec2 uv = vUv + vec2(float(x), float(y)) * uTexel;
      float z = -viewPosition(uv, readDepth(uv)).z;
      float w = step(abs(z - z0), 0.05 * z0);
      sum += texture2D(tAO, uv).r * w;
      weight += w;
    }
  }
  gl_FragColor = vec4(sum / max(weight, 1.0), 0.0, 0.0, 1.0);
}
`;

/** Bloom seed: the scene where its luminance clears the threshold, feathered by `smoothing`. */
export const luminanceFragment = /* glsl */ `
uniform sampler2D tInput;
uniform float uThreshold;
uniform float uSmoothing;
varying vec2 vUv;
void main() {
  vec3 c = texture2D(tInput, vUv).rgb;
  float l = dot(c, vec3(0.2126, 0.7152, 0.0722));
  float m = smoothstep(uThreshold, uThreshold + uSmoothing, l);
  gl_FragColor = vec4(c * m, 1.0);
}
`;

/** 13-tap downsample (the "Call of Duty" filter): stable under motion, no fireflies. */
export const downsampleFragment = /* glsl */ `
uniform sampler2D tInput;
uniform vec2 uTexel;
varying vec2 vUv;
vec3 s(float x, float y) { return texture2D(tInput, vUv + vec2(x, y) * uTexel).rgb; }
void main() {
  vec3 a = s(-2.0, 2.0), b = s(0.0, 2.0), c = s(2.0, 2.0);
  vec3 d = s(-2.0, 0.0), e = s(0.0, 0.0), f = s(2.0, 0.0);
  vec3 g = s(-2.0, -2.0), h = s(0.0, -2.0), i = s(2.0, -2.0);
  vec3 j = s(-1.0, 1.0), k = s(1.0, 1.0), l = s(-1.0, -1.0), m = s(1.0, -1.0);
  vec3 o = e * 0.125 + (a + c + g + i) * 0.03125 + (b + d + f + h) * 0.0625 + (j + k + l + m) * 0.125;
  gl_FragColor = vec4(o, 1.0);
}
`;

/** 9-tap tent upsample of the level below, mixed over this level's downsample by `radius`. */
export const upsampleFragment = /* glsl */ `
uniform sampler2D tInput;
uniform sampler2D tSupport;
uniform vec2 uTexel;
uniform float uRadius;
varying vec2 vUv;
vec3 s(float x, float y) { return texture2D(tInput, vUv + vec2(x, y) * uTexel).rgb; }
void main() {
  vec3 tent = (s(-1.0, 1.0) + 2.0 * s(0.0, 1.0) + s(1.0, 1.0)
    + 2.0 * s(-1.0, 0.0) + 4.0 * s(0.0, 0.0) + 2.0 * s(1.0, 0.0)
    + s(-1.0, -1.0) + 2.0 * s(0.0, -1.0) + s(1.0, -1.0)) / 16.0;
  gl_FragColor = vec4(mix(texture2D(tSupport, vUv).rgb, tent, uRadius), 1.0);
}
`;

/**
 * God-ray occlusion mask: the light's disc where the sky is (depth at the clear value), black where
 * anything was drawn. The disc is the old god-rays mesh's angular size (PostPipeline.prepareRays).
 */
export const raysMaskFragment = /* glsl */ `
uniform sampler2D tDepth;
uniform mat4 uInvProj;
uniform mat4 uCamWorld;
uniform vec3 uLightDir;
uniform vec3 uLightColor;
// Cosines of the disc's soft edge (uCosEdge, further out) and its full core (uCosCore).
uniform float uCosEdge;
uniform float uCosCore;
varying vec2 vUv;
void main() {
  if (texture2D(tDepth, vUv).r < 1.0) { gl_FragColor = vec4(0.0, 0.0, 0.0, 1.0); return; }
  vec4 v = uInvProj * vec4(vUv * 2.0 - 1.0, 1.0, 1.0);
  vec3 dir = normalize((uCamWorld * vec4(v.xyz / v.w, 0.0)).xyz);
  float disc = smoothstep(uCosEdge, uCosCore, dot(dir, uLightDir));
  gl_FragColor = vec4(uLightColor * disc, 1.0);
}
`;

/** Radial blur toward the light: the classic density / decay / weight / exposure march. */
export const raysRadialFragment = /* glsl */ `
uniform sampler2D tInput;
uniform vec2 uLightUv;
uniform float uDensity;
uniform float uDecay;
uniform float uWeight;
uniform float uExposure;
uniform float uClampMax;
uniform int uSamples;
varying vec2 vUv;
void main() {
  vec2 delta = (vUv - uLightUv) * (uDensity / float(uSamples));
  vec2 uv = vUv;
  float illumination = 1.0;
  vec3 sum = vec3(0.0);
  for (int i = 0; i < uSamples; i++) {
    uv -= delta;
    sum += texture2D(tInput, uv).rgb * illumination * uWeight;
    illumination *= uDecay;
  }
  gl_FragColor = vec4(min(sum * uExposure, uClampMax), 1.0);
}
`;

/** One Kawase step: four diagonal taps at `offset` texels. */
export const kawaseFragment = /* glsl */ `
uniform sampler2D tInput;
uniform vec2 uTexel;
uniform float uOffset;
varying vec2 vUv;
void main() {
  vec2 o = uTexel * uOffset;
  vec3 c = texture2D(tInput, vUv + o).rgb + texture2D(tInput, vUv - o).rgb
    + texture2D(tInput, vUv + vec2(o.x, -o.y)).rgb + texture2D(tInput, vUv + vec2(-o.x, o.y)).rgb;
  gl_FragColor = vec4(c * 0.25, 1.0);
}
`;

/**
 * The composite: scene × AO (depth-aware upsample of the half-resolution AO), screen-blended bloom
 * and rays, the saturation grade, then ONE sRGB encode for the canvas. The stages are uniforms, not
 * defines, so the whole pipeline is one program whatever the preset.
 */
export const compositeFragment = /* glsl */ `
${depthGlsl}
uniform sampler2D tScene;
uniform sampler2D tAO;
uniform sampler2D tBloom;
uniform sampler2D tRays;
uniform vec2 uAoTexel;
uniform float uUseAO;
uniform float uUseBloom;
uniform float uUseRays;
uniform float uAoIntensity;
uniform float uBloomIntensity;
uniform float uGrade;
varying vec2 vUv;

// postprocessing's hue-saturation grade with hue 0, kept verbatim so the look does not move.
vec3 applyGrade(vec3 color) {
  float average = (color.r + color.g + color.b) / 3.0;
  vec3 diff = average - color;
  if (uGrade > 0.0) { color += diff * (1.0 - 1.0 / (1.001 - uGrade)); }
  else { color += diff * -uGrade; }
  return min(color, 1.0);
}

float upsampledAO() {
  float z0 = -viewPosition(vUv, readDepth(vUv)).z;
  vec2 base = (floor(vUv / uAoTexel - 0.5) + 0.5) * uAoTexel;
  float sum = 0.0, weight = 0.0;
  for (int y = 0; y <= 1; y++) {
    for (int x = 0; x <= 1; x++) {
      vec2 uv = base + vec2(float(x), float(y)) * uAoTexel;
      float z = -viewPosition(uv, readDepth(uv)).z;
      float w = 1.0 / (1.0 + 20.0 * abs(z - z0) / max(z0, 1e-3));
      sum += texture2D(tAO, uv).r * w;
      weight += w;
    }
  }
  return sum / max(weight, 1e-4);
}

void main() {
  vec3 color = texture2D(tScene, vUv).rgb;
  if (uUseAO > 0.5) color *= 1.0 - clamp(upsampledAO() * uAoIntensity, 0.0, 1.0);
  if (uUseBloom > 0.5) {
    vec3 b = clamp(texture2D(tBloom, vUv).rgb * uBloomIntensity, 0.0, 1.0);
    color = 1.0 - (1.0 - color) * (1.0 - b);
  }
  if (uUseRays > 0.5) {
    vec3 r = clamp(texture2D(tRays, vUv).rgb, 0.0, 1.0);
    color = 1.0 - (1.0 - color) * (1.0 - r);
  }
  gl_FragColor = vec4(applyGrade(color), 1.0);
  #include <colorspace_fragment>
}
`;
